import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { api } from '../services/api';
import { getRoleLabel, getRoleColor } from '../services/mockUsers';

interface Notification {
  id: string;
  sender_id: string;
  sender_name: string;
  recipient_ids: string[];
  recipient_names: string[];
  subject: string;
  content: string;
  created_at: string;
  is_read: boolean;
}

const Communication: React.FC = () => {
  const { user, session } = useAuth();
  const { isAdmin, isAgence, isSOBEBRA } = usePermissions();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [sentNotifications, setSentNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [recipientType, setRecipientType] = useState<'individual' | 'role'>('individual');
  const [selectedRole, setSelectedRole] = useState<'agent_terrain' | 'agence' | 'soebra'>('agent_terrain');
  const [formData, setFormData] = useState({
    subject: '',
    content: '',
  });
  const [users, setUsers] = useState<any[]>([]);

  const canSendNotifications = isAdmin || isAgence || isSOBEBRA;

  const getUsersByRole = (role: 'agent_terrain' | 'agence' | 'soebra'): any[] => {
    return users.filter((u: any) => u.role === role);
  };

  useEffect(() => {
    // Attendre que la session soit disponible avant de charger
    if (session && user) {
      const loadData = async () => {
        try {
          setLoading(true);
          const { users: allUsers } = await api.users.getAll();
          setUsers(allUsers);
          await loadNotifications();
          // Charger l'historique des notifications envoyées pour les admins
          if (canSendNotifications) {
            await loadSentNotifications();
          }
        } catch (error) {
          console.error('Erreur lors du chargement des données:', error);
        } finally {
          setLoading(false);
        }
      };
      loadData();
    }
  }, [session, user]);

  const loadNotifications = async () => {
    if (!session || !user) {
      console.warn('Session ou utilisateur non disponible pour charger les notifications');
      return;
    }
    try {
      console.log('Chargement des notifications pour l\'utilisateur:', user.id);
      const response = await api.notifications.getAll();
      console.log('Notifications reçues:', response.notifications?.length || 0);
      
      // Transformer les notifications Supabase en format Notification
      const transformedNotifications: Notification[] = (response.notifications || []).map((n: any) => ({
        id: n.id,
        sender_id: n.sender_id || '',
        sender_name: n.sender_name || 'Système',
        recipient_ids: n.user_id ? [n.user_id] : [],
        recipient_names: [],
        subject: n.title || '',
        content: n.message || '',
        created_at: n.created_at,
        is_read: n.is_read || false,
      }));
      
      // Trier : non lues en premier, puis par date décroissante
      const sortedNotifications = transformedNotifications.sort((a, b) => {
        if (a.is_read !== b.is_read) {
          return a.is_read ? 1 : -1;
        }
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      
      console.log('Notifications triées:', sortedNotifications.length);
      setNotifications(sortedNotifications);
    } catch (error: any) {
      console.error('Erreur lors du chargement des notifications:', error);
      console.error('Détails de l\'erreur:', error.message, error.code);
      setNotifications([]);
    }
  };

  const loadSentNotifications = async () => {
    if (!session || !user) {
      return;
    }
    try {
      const response = await api.notifications.getSent();
      // Grouper les notifications par titre/message/date pour regrouper les envois en masse
      const grouped = new Map<string, any>();
      (response.notifications || []).forEach((n: any) => {
        // Créer une clé basée sur le titre, message et date (arrondie à la minute)
        const date = new Date(n.created_at);
        const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}-${date.getMinutes()}`;
        const key = `${n.title}-${n.message}-${dateKey}`;
        
        if (!grouped.has(key)) {
          grouped.set(key, {
            ...n,
            recipients: [],
          });
        }
        // Ajouter le destinataire à la liste
        if (n.recipient && n.recipient.id) {
          const existingRecipient = grouped.get(key).recipients.find((r: any) => r.id === n.recipient.id);
          if (!existingRecipient) {
            grouped.get(key).recipients.push(n.recipient);
          }
        }
      });
      setSentNotifications(Array.from(grouped.values()).sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ));
    } catch (error: any) {
      console.error('Erreur lors du chargement des notifications envoyées:', error);
      setSentNotifications([]);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await api.notifications.markAsRead(notificationId);
      // Mettre à jour localement immédiatement pour une meilleure UX
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      // Recharger après un court délai pour synchroniser avec la base de données
      setTimeout(async () => {
        await loadNotifications();
      }, 300);
    } catch (error: any) {
      console.error('Erreur lors du marquage comme lue:', error);
      alert('Erreur lors du marquage: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const handleSendNotification = async () => {
    if (!formData.subject || !formData.content) return;

    let recipients: any[] = [];

    if (recipientType === 'role') {
      // Récupérer tous les utilisateurs du rôle sélectionné
      recipients = users.filter((u: any) => u.role === selectedRole);
    } else {
      // Récupérer les utilisateurs sélectionnés individuellement
      recipients = selectedRecipients
        .map(id => users.find((u: any) => u.id === id))
        .filter(Boolean) as any[];
    }

    if (recipients.length === 0) {
      alert('Veuillez sélectionner au moins un destinataire');
      return;
    }

    try {
      // Envoyer une notification à chaque destinataire
      const promises = recipients.map(recipient => 
        api.notifications.create({
          user_id: recipient.id,
          sender_id: user?.id,
          type: 'message',
          title: formData.subject,
          message: formData.content,
          link: '/communication',
        })
      );

      await Promise.all(promises);

      alert(`Notification envoyée à ${recipients.length} destinataire(s)`);

      // Réinitialiser le formulaire
      setFormData({ subject: '', content: '' });
      setSelectedRecipients([]);
      setOpenDialog(false);

      // Recharger les notifications après un court délai pour laisser le temps à la base de données
      setTimeout(async () => {
        await loadNotifications();
        if (canSendNotifications) {
          await loadSentNotifications();
        }
      }, 500);
    } catch (error: any) {
      console.error('Erreur lors de l\'envoi de la notification:', error);
      alert('Erreur lors de l\'envoi: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const handleRecipientToggle = (userId: string) => {
    if (selectedRecipients.includes(userId)) {
      setSelectedRecipients(selectedRecipients.filter(id => id !== userId));
    } else {
      setSelectedRecipients([...selectedRecipients, userId]);
    }
  };

  const handleSelectAll = () => {
    const allUserIds = users
      .filter((u: any) => u.id !== user?.id)
      .map((u: any) => u.id);
    setSelectedRecipients(allUserIds);
  };

  const handleDeselectAll = () => {
    setSelectedRecipients([]);
  };

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {user?.role === 'agent_terrain' ? 'Notifications' : 'Communication'}
        </Typography>
        {canSendNotifications && (
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => setOpenDialog(true)}
          >
            Envoyer une notification
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Historique des Notifications
          </Typography>
          {!loading && (
            <Typography variant="body2" color="text.secondary">
              {notifications.filter(n => !n.is_read).length} non lue(s) sur {notifications.length}
            </Typography>
          )}
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : notifications.length === 0 ? (
          <Alert severity="info">Aucune notification pour le moment</Alert>
        ) : (
          <List>
            {notifications.map((notification) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  sx={{
                    bgcolor: notification.is_read ? 'transparent' : 'action.hover',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      bgcolor: notification.is_read ? 'action.hover' : 'action.selected',
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: notification.is_read ? 'grey.400' : 'primary.main' }}>
                      <NotificationsIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1" fontWeight={notification.is_read ? 'normal' : 'bold'}>
                            {notification.subject}
                          </Typography>
                          {!notification.is_read && (
                            <Chip label="Non lu" size="small" color="primary" />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="text.secondary">
                            De: {notification.sender_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            • {formatTime(notification.created_at)}
                          </Typography>
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {notification.content}
                      </Typography>
                    }
                  />
                  {user?.role === 'agent_terrain' && (
                    <Button
                      variant={notification.is_read ? "outlined" : "contained"}
                      color={notification.is_read ? undefined : "primary"}
                      startIcon={notification.is_read ? <CheckCircleIcon /> : <CircleIcon />}
                      onClick={() => handleMarkAsRead(notification.id)}
                      size="small"
                      sx={{ ml: 2 }}
                    >
                      {notification.is_read ? 'Marqué comme lu' : 'Marquer comme lu'}
                    </Button>
                  )}
                  {user?.role !== 'agent_terrain' && (
                    <Tooltip title={notification.is_read ? 'Marquer comme non lue' : 'Marquer comme lue'}>
                      <IconButton
                        onClick={() => handleMarkAsRead(notification.id)}
                        color={notification.is_read ? 'default' : 'primary'}
                      >
                        {notification.is_read ? (
                          <CheckCircleIcon />
                        ) : (
                          <CircleIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Historique des notifications envoyées - Uniquement pour les admins */}
      {canSendNotifications && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" mb={2}>
            Notifications Envoyées
          </Typography>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : sentNotifications.length === 0 ? (
            <Alert severity="info">Aucune notification envoyée pour le moment</Alert>
          ) : (
            <List>
              {sentNotifications.map((notification, index) => (
                <React.Fragment key={`sent-${notification.id || index}`}>
                  <ListItem
                    sx={{
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'success.main' }}>
                        <SendIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {notification.title}
                            </Typography>
                            <Chip 
                              label={`${notification.recipients?.length || 1} destinataire(s)`} 
                              size="small" 
                              color="success" 
                            />
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary">
                              À: {notification.recipients?.map((r: any) => r?.name || 'Inconnu').join(', ') || 'Destinataires'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              • {formatTime(notification.created_at)}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {notification.message}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      )}

      {/* Dialog Envoi de Notification - Uniquement pour ceux qui ont les permissions */}
      {canSendNotifications && (
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Envoyer une Notification
          <IconButton
            onClick={() => setOpenDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Cette notification sera envoyée aux destinataires sélectionnés et apparaîtra dans leur liste de notifications.
          </Alert>

          {/* Type de sélection */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Type de destinataires</InputLabel>
            <Select
              value={recipientType}
              onChange={(e) => {
                setRecipientType(e.target.value as 'individual' | 'role');
                setSelectedRecipients([]);
              }}
            >
              <MenuItem value="individual">Sélection individuelle</MenuItem>
              <MenuItem value="role">Par rôle (tous les membres d'un rôle)</MenuItem>
            </Select>
          </FormControl>

          {/* Sélection par rôle */}
          {recipientType === 'role' && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Rôle cible</InputLabel>
              <Select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value as any)}
              >
                <MenuItem value="agent_terrain">Tous les Agents Terrain</MenuItem>
                <MenuItem value="agence">Tous les membres de l'Agence</MenuItem>
                <MenuItem value="soebra">Tous les membres SOBEBRA</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Sélection individuelle */}
          {recipientType === 'individual' && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2">
                  Sélectionner les destinataires ({selectedRecipients.length} sélectionné(s))
                </Typography>
                <Box>
                  <Button size="small" onClick={handleSelectAll}>
                    Tout sélectionner
                  </Button>
                  <Button size="small" onClick={handleDeselectAll}>
                    Tout désélectionner
                  </Button>
                </Box>
              </Box>
              <Paper sx={{ maxHeight: 300, overflow: 'auto', p: 1 }}>
                <List dense>
                  {users
                    .filter((u: any) => u.id !== user?.id)
                    .map((systemUser: any) => (
                      <ListItem key={systemUser.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedRecipients.includes(systemUser.id)}
                              onChange={() => handleRecipientToggle(systemUser.id)}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 24, height: 24 }}>
                                {systemUser.name.charAt(0)}
                              </Avatar>
                              <Typography variant="body2">{systemUser.name}</Typography>
                              <Chip
                                label={getRoleLabel(systemUser.role)}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                  bgcolor: getRoleColor(systemUser.role),
                                  color: 'white',
                                }}
                              />
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                </List>
              </Paper>
            </Box>
          )}

          {/* Aperçu du nombre de destinataires */}
          {recipientType === 'role' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Cette notification sera envoyée à {getUsersByRole(selectedRole).length} utilisateur(s) ({getRoleLabel(selectedRole)})
            </Alert>
          )}

          {/* Formulaire */}
          <TextField
            fullWidth
            label="Sujet"
            margin="normal"
            required
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
          />
          <TextField
            fullWidth
            label="Message"
            margin="normal"
            required
            multiline
            rows={6}
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            placeholder="Rédigez votre message de notification..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleSendNotification}
            disabled={
              !formData.subject ||
              !formData.content ||
              (recipientType === 'individual' && selectedRecipients.length === 0)
            }
            startIcon={<SendIcon />}
          >
            Envoyer
          </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default Communication;
